import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Terminal, Loader2, Trash2, Copy, Check, RotateCcw, ChevronDown, Maximize2, Minimize2, ImagePlus, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_ACTIONS = [
  "Generate a week of content",
  "What's performing best?",
  "Approve all queued posts",
  "Show queue status",
];

function formatTime(date) {
  return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function cleanActionBlocks(text) {
  let cleaned = text.replace(/```action\s*\{[\s\S]*?\}[\s\S]*?```/g, "");
  cleaned = cleaned.replace(/\{"action"\s*:\s*"[^"]+"\s*,\s*"params"\s*:\s*\{[^}]*\}\s*\}/g, "");
  cleaned = cleaned.replace(/\[ACTION_RESULT:[^\]]+\]/g, "");
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();
  return cleaned;
}

// Code block with copy button
function CodeBlock({ language, children }) {
  const [copied, setCopied] = useState(false);
  const code = String(children).replace(/\n$/, "");

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="jarvis-code-block" data-testid="jarvis-code-block">
      <div className="jarvis-code-header">
        <span className="jarvis-code-lang">{language || "text"}</span>
        <button onClick={handleCopy} className="jarvis-code-copy" data-testid="jarvis-copy-code">
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      <SyntaxHighlighter
        language={language || "text"}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: "12px",
          fontSize: "11px",
          lineHeight: 1.5,
          background: "#151820",
          borderRadius: "0 0 8px 8px",
        }}
        wrapLongLines
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

// Follow-up suggestion chips
function SuggestionChips({ suggestions, onSelect, disabled }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="jarvis-suggestions" data-testid="jarvis-suggestions">
      {suggestions.map((s, i) => (
        <button
          key={i}
          onClick={() => onSelect(s)}
          disabled={disabled}
          className="jarvis-suggestion-chip"
          data-testid={`jarvis-suggestion-${i}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// Markdown components for ReactMarkdown
const markdownComponents = {
  h1: ({ children }) => <h3 className="text-sm font-bold text-zinc-100 mt-3 mb-1.5">{children}</h3>,
  h2: ({ children }) => <h3 className="text-sm font-bold text-zinc-100 mt-3 mb-1.5">{children}</h3>,
  h3: ({ children }) => <h4 className="text-xs font-bold text-zinc-200 mt-2.5 mb-1">{children}</h4>,
  h4: ({ children }) => <h4 className="text-xs font-semibold text-zinc-300 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-[1.7]">{children}</p>,
  strong: ({ children }) => <strong className="text-zinc-100 font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-zinc-400">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-zinc-300 leading-[1.6]">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="text-teal underline hover:text-teal/80 transition-colors" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="border-t border-[#2a2e36] my-3" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[#c6a96f50] pl-3 my-2 text-zinc-400 italic">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="jarvis-table">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="jarvis-thead">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="jarvis-tr">{children}</tr>,
  th: ({ children }) => <th className="jarvis-th">{children}</th>,
  td: ({ children }) => <td className="jarvis-td">{children}</td>,
  code: ({ inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || "");
    if (!inline && (match || String(children).includes("\n"))) {
      return <CodeBlock language={match?.[1]}>{children}</CodeBlock>;
    }
    return (
      <code className="bg-[#21252b] text-[#c6a96f] px-1 py-0.5 rounded text-[11px] font-mono" {...props}>
        {children}
      </code>
    );
  },
};

// Action result card -- renders message through markdown
function ActionResultCard({ action, success, message }) {
  return (
    <div className={`jarvis-action-card ${success ? "success" : "failure"}`} data-testid="jarvis-action-card">
      <div className="jarvis-action-card-header">
        <span className={`jarvis-action-dot ${success ? "bg-emerald-500" : "bg-red-400"}`} />
        <span className="jarvis-action-name">{action.replace(/_/g, " ")}</span>
      </div>
      <div className="jarvis-action-msg">
        <ReactMarkdown components={markdownComponents}>{message}</ReactMarkdown>
      </div>
    </div>
  );
}

export default function JarvisPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [retryMessage, setRetryMessage] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // {base64, preview, name}
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  // Sync expanded state to CSS variable on main-content
  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.style.marginRight = expanded ? "900px" : "340px";
    }
  }, [expanded]);

  // Load conversation history on mount
  useEffect(() => {
    const token = localStorage.getItem("studio_token");
    if (!token) return;
    fetch(`${BACKEND_URL}/api/jarvis/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((data) => {
        const loaded = (data.messages || []).map((m) => ({
          role: m.role,
          text: m.content || "",
          time: m.timestamp || new Date().toISOString(),
          actionResults: m.action_results || null,
        }));
        if (loaded.length > 0) {
          setMessages(loaded);
        } else {
          setMessages([
            {
              role: "assistant",
              text: "Online. I have full context on all 6 brands, the content pipeline, ad accounts, and your approval queue. What do you need?",
              time: new Date().toISOString(),
            },
          ]);
        }
        setHistoryLoaded(true);
      })
      .catch(() => {
        setMessages([
          {
            role: "assistant",
            text: "Online. Ready to operate.",
            time: new Date().toISOString(),
          },
        ]);
        setHistoryLoaded(true);
      });
  }, []);

  // Auto scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  // Scroll button visibility
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      setShowScrollBtn(!atBottom);
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const clearHistory = async () => {
    const token = localStorage.getItem("studio_token");
    try {
      await fetch(`${BACKEND_URL}/api/jarvis/history/clear`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    setMessages([
      {
        role: "assistant",
        text: "Conversation cleared. What do you need?",
        time: new Date().toISOString(),
      },
    ]);
    setSuggestions([]);
  };

  const getCurrentPage = () => {
    const path = window.location.pathname;
    const map = {
      "/": "dashboard",
      "/social": "social_overview",
      "/library": "content_library",
      "/queue": "approval_queue",
      "/calendar": "content_calendar",
      "/analytics": "analytics",
      "/ads": "ads_manager",
      "/strategy": "strategy",
      "/settings": "settings",
    };
    return map[path] || "unknown";
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1]; // strip data:image/...;base64,
      setPendingImage({ base64, preview: reader.result, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const sendMessage = useCallback(
    async (text, isRetry = false) => {
      if ((!text.trim() && !pendingImage) || streaming) return;
      setRetryMessage(null);
      setSuggestions([]);

      const imageToSend = pendingImage;
      const userMsg = {
        role: "user",
        text: text.trim() || (imageToSend ? "(screenshot attached)" : ""),
        time: new Date().toISOString(),
        imagePreview: imageToSend?.preview || null,
      };
      if (!isRetry) {
        setMessages((prev) => [...prev, userMsg]);
      }
      setInput("");
      setPendingImage(null);
      setStreaming(true);
      // Force-clear the textarea DOM element in case React batching delays the state update
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.style.height = "auto";
      }

      const assistantMsg = { role: "assistant", text: "", time: new Date().toISOString(), actionResults: [] };
      setMessages((prev) => [...prev, assistantMsg]);

      const token = localStorage.getItem("studio_token");
      let retryCount = 0;
      const maxRetries = 1;

      const attemptFetch = async () => {
        try {
          const bodyPayload = {
            message: text.trim() || "Analyze this image",
            context: getCurrentPage(),
          };
          if (imageToSend) {
            bodyPayload.image = imageToSend.base64;
          }

          const resp = await fetch(`${BACKEND_URL}/api/ai/rook/stream`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(bodyPayload),
          });

          if (resp.status === 401) {
            localStorage.removeItem("studio_token");
            window.location.href = "/login";
            return;
          }
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
              try {
                const json = JSON.parse(line.slice(6));

                // Handle action results
                if (json.action_result) {
                  const ar = json.action_result;
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last.role === "assistant") {
                      const results = [...(last.actionResults || []), ar];
                      updated[updated.length - 1] = { ...last, actionResults: results };
                    }
                    return updated;
                  });
                  continue;
                }

                // Handle follow-up suggestions
                if (json.suggestions) {
                  setSuggestions(json.suggestions);
                  continue;
                }

                // Handle text content
                const content = json.choices?.[0]?.delta?.content || "";
                if (content) {
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last.role === "assistant") {
                      updated[updated.length - 1] = { ...last, text: last.text + content };
                    }
                    return updated;
                  });
                }
              } catch {
                // skip unparseable
              }
            }
          }
        } catch (e) {
          if (retryCount < maxRetries) {
            retryCount++;
            await new Promise((r) => setTimeout(r, 1500));
            return attemptFetch();
          }
          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last.role === "assistant" && !last.text) {
              updated[updated.length - 1] = {
                ...last,
                text: "Connection failed. Check that the backend is running.",
                isError: true,
              };
            }
            return updated;
          });
          setRetryMessage(text.trim());
        }
      };

      await attemptFetch();
      setStreaming(false);
    },
    [streaming, pendingImage]
  );

  const hasUserMessages = messages.some((m) => m.role === "user");

  if (!historyLoaded) {
    return (
      <div className={`rook-fixed ${expanded ? "expanded" : ""}`} data-testid="jarvis-panel">
        <div className="flex items-center justify-center h-full">
          <Loader2 size={20} className="animate-spin text-[#c6a96f]" />
        </div>
      </div>
    );
  }

  return (
    <div className={`rook-fixed ${expanded ? "expanded" : ""}`} data-testid="jarvis-panel">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#222630] flex-shrink-0">
        <Terminal size={14} className="text-gold" />
        <span
          className="font-mono text-[10px] uppercase tracking-[0.3em]"
          style={{ color: "rgba(198,169,111,0.6)" }}
        >
          // jarvis
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-1.5 rounded-lg text-zinc-600 hover:text-zinc-400 hover:bg-[#1a1d21] transition-all"
            title={expanded ? "Collapse panel" : "Expand panel"}
            data-testid="jarvis-expand-btn"
          >
            {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          <button
            onClick={clearHistory}
            className="p-1.5 rounded-lg text-zinc-600 hover:text-zinc-400 hover:bg-[#1a1d21] transition-all"
            title="Clear conversation"
            data-testid="jarvis-clear-btn"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 relative">
        {messages.map((msg, i) => (
          <div key={i} className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"} animate-fadeIn`}>
            <div
              className={`max-w-[92%] rounded-xl px-3.5 py-2.5 text-[13px] leading-[1.65] ${
                msg.role === "user"
                  ? "bg-[#c6a96f12] text-zinc-200 border border-[#c6a96f25]"
                  : msg.isError
                  ? "bg-[#2a1515] text-red-300 border border-red-900/40"
                  : "bg-[#1a1d21] text-zinc-300 border border-[#2a2e36]"
              }`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {msg.role === "assistant" ? (
                <>
                  <ReactMarkdown components={markdownComponents}>
                    {cleanActionBlocks(msg.text)}
                  </ReactMarkdown>
                  {/* Action result cards */}
                  {msg.actionResults &&
                    msg.actionResults.length > 0 &&
                    msg.actionResults.map((ar, j) => (
                      <ActionResultCard
                        key={j}
                        action={ar.action}
                        success={ar.success}
                        message={ar.message}
                      />
                    ))}
                </>
              ) : (
                <>
                  {msg.imagePreview && (
                    <img src={msg.imagePreview} alt="attached" className="rounded-lg mb-2 max-h-[160px] object-contain" />
                  )}
                  {msg.text}
                </>
              )}
              {streaming && i === messages.length - 1 && msg.role === "assistant" && (
                <span className="inline-block w-[6px] h-[14px] ml-0.5 bg-gold/70 animate-pulse" />
              )}
            </div>
            <span
              className="text-[10px] mt-1 px-1"
              style={{ fontFamily: "'Azeret Mono', monospace", color: "#3f3f46" }}
            >
              {formatTime(msg.time)}
            </span>
          </div>
        ))}

        {/* Follow-up suggestions */}
        {!streaming && suggestions.length > 0 && (
          <SuggestionChips
            suggestions={suggestions}
            onSelect={(s) => sendMessage(s)}
            disabled={streaming}
          />
        )}

        {/* Retry button */}
        {!streaming && retryMessage && (
          <div className="flex justify-center">
            <button
              onClick={() => sendMessage(retryMessage, true)}
              className="flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-[#c6a96f] transition-colors px-3 py-1.5 rounded-full border border-[#2a2e36] hover:border-[#c6a96f40]"
              data-testid="jarvis-retry-btn"
            >
              <RotateCcw size={12} />
              Retry
            </button>
          </div>
        )}

        <div ref={bottomRef} />

        {/* Scroll to bottom button */}
        {showScrollBtn && (
          <button
            onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
            className="sticky bottom-2 left-1/2 -translate-x-1/2 p-1.5 rounded-full bg-[#21252b] border border-[#2a2e36] text-zinc-500 hover:text-[#c6a96f] transition-all shadow-lg z-10"
          >
            <ChevronDown size={14} />
          </button>
        )}
      </div>

      {/* Quick actions */}
      {!hasUserMessages && (
        <div className="px-4 pb-3 flex-shrink-0">
          <p
            className="text-[10px] uppercase tracking-[0.3em] mb-2"
            style={{ fontFamily: "'Azeret Mono', monospace", color: "rgba(198,169,111,0.5)" }}
          >
            // quick actions
          </p>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action}
                onClick={() => sendMessage(action)}
                className="text-[11px] px-2.5 py-1.5 rounded-full border border-[#2a2e36] text-zinc-500 hover:text-gold hover:border-[#c6a96f40] transition-all duration-200"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                data-testid={`jarvis-chip-${action.split(" ").slice(0, 3).join("-").toLowerCase()}`}
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-[#222630] flex-shrink-0">
        {/* Pending image preview */}
        {pendingImage && (
          <div className="flex items-center gap-2 mb-2 px-1">
            <img src={pendingImage.preview} alt="pending" className="h-12 w-12 rounded-lg object-cover border border-[#2a2e36]" />
            <span className="text-[11px] text-zinc-500 truncate flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{pendingImage.name}</span>
            <button onClick={() => setPendingImage(null)} className="p-1 text-zinc-600 hover:text-red-400 transition-colors" data-testid="jarvis-remove-image">
              <X size={14} />
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
          data-testid="jarvis-file-input"
        />
        <div className="flex items-end gap-2 bg-[#1a1d21] border border-[#2a2e36] rounded-xl px-3.5 focus-within:border-[#a08a58] transition-colors">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 rounded-lg text-zinc-600 hover:text-[#c6a96f] transition-all self-center"
            title="Attach screenshot"
            data-testid="jarvis-upload-btn"
          >
            <ImagePlus size={16} />
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder={pendingImage ? "Add a question about this image..." : "Ask Jarvis..."}
            rows={1}
            className="flex-1 bg-transparent text-zinc-200 text-[13px] py-3 outline-none placeholder:text-zinc-600 resize-none overflow-y-auto"
            style={{ fontFamily: "'DM Sans', sans-serif", maxHeight: "120px" }}
            data-testid="jarvis-input"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={streaming || (!input.trim() && !pendingImage)}
            className="p-1.5 rounded-lg transition-all duration-200 disabled:opacity-30"
            style={{ background: !streaming && (input.trim() || pendingImage) ? "#c6a96f" : "transparent" }}
            data-testid="jarvis-send-btn"
          >
            {streaming ? (
              <Loader2 size={15} className="animate-spin text-gold" />
            ) : (
              <Send size={15} className={input.trim() ? "text-[#0f1114]" : "text-zinc-600"} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
